import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const ProfileSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 26px;
  margin-left: 10px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 34px;
    
    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background-color: #007AFF;
  }
  
  input:checked + span:before {
    transform: translateX(26px);
  }
`;

const Textarea = styled.textarea`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-weight: bold;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const Button = styled.button`
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &.primary {
    background: #007AFF;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #666;
    border: 1px solid #dee2e6;
    
    &:hover {
      background: #e9ecef;
    }
  }
`;

const InfoBox = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 6px;
  padding: 15px;
  margin-top: 15px;
  font-size: 13px;
  color: #0066cc;
  line-height: 1.5;
`;

const AgentProfile = () => {
  const [profileData, setProfileData] = useState({
    agent_name: '',
    agent_phone: '',
    agent_email: '',
    agent_license: '',
    brokerage_name: '',
    bio: '',
    profile_photo_url: '',
    is_public: false
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    loadAgentProfile();
  }, []);

  const loadAgentProfile = async () => {
    try {
      console.log('📋 Loading agent profile from server...');
      console.log('🔐 Current user:', auth.currentUser?.email);
      const profile = await showingbookerAPI.getAgentProfile();
      
      console.log('📋 Raw profile response:', profile);
      
      // Check if profile has meaningful data
      const hasData = profile.agent_name || profile.agent_phone || profile.agent_license;
      
      setProfileData(profile);
      setHasProfile(hasData);
      
      if (!hasData) {
        console.log('📋 No agent profile data found - enabling edit mode');
        setIsEditing(true); // Auto-edit mode for new users
      } else {
        console.log('📋 Loaded agent profile for user:', auth.currentUser?.email, profile);
      }
    } catch (error) {
      console.error('Error loading agent profile:', error);
      // If API fails, still show edit mode for new profile creation
      setIsEditing(true);
    }
  };

  const saveAgentProfile = async (data) => {
    try {
      console.log('💾 Saving agent profile to server for user:', auth.currentUser?.email);
      console.log('💾 Profile data being saved:', data);
      const updatedProfile = await showingbookerAPI.updateAgentProfile(data);
      console.log('✅ Agent profile saved successfully for user:', auth.currentUser?.email, updatedProfile);
      return updatedProfile;
    } catch (error) {
      console.error('❌ Error saving agent profile:', error);
      throw error;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!profileData.agent_name || !profileData.agent_phone) {
      alert('Please fill in at least your name and phone number.');
      return;
    }
    
    try {
      await saveAgentProfile(profileData);
      setHasProfile(true);
      setIsEditing(false);
      alert('✅ Agent profile saved! This information will appear on all your property booking pages.');
    } catch (error) {
      alert('❌ Failed to save agent profile. Please try again.');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = async () => {
    if (hasProfile) {
      await loadAgentProfile(); // Reset to saved data
      setIsEditing(false);
    }
  };

  if (!isEditing && hasProfile) {
    return (
      <ProfileSection>
        <SectionTitle>👤 Agent/Landlord Information</SectionTitle>
        
        <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
          <div>
            <Label>Name:</Label>
            <div style={{ padding: '8px 0', fontSize: '16px', fontWeight: '500' }}>
              {profileData.agent_name}
            </div>
          </div>
          
          <div>
            <Label>Phone:</Label>
            <div style={{ padding: '8px 0', fontSize: '16px' }}>
              {profileData.agent_phone}
            </div>
          </div>
          
          {profileData.agent_email && (
            <div>
              <Label>Email:</Label>
              <div style={{ padding: '8px 0', fontSize: '16px' }}>
                {profileData.agent_email}
              </div>
            </div>
          )}
          
          {profileData.agent_license && (
            <div>
              <Label>License #:</Label>
              <div style={{ padding: '8px 0', fontSize: '16px' }}>
                {profileData.agent_license}
              </div>
            </div>
          )}
        </div>

        <ButtonRow>
          <Button className="primary" onClick={handleEdit}>
            ✏️ Edit Profile
          </Button>
          {profileData.is_public && (
            <Button 
              className="secondary" 
              onClick={() => window.location.href = `/agent/${profileData.agent_name?.toLowerCase().replace(/\s+/g, '-')}`}
            >
              👥 View Public Profile
            </Button>
          )}
        </ButtonRow>
        
        <div>
          <SectionTitle style={{ marginTop: '20px', alignItems: 'center' }}>
            Public Profile
            <Toggle>
              <input
                type="checkbox"
                checked={profileData.is_public}
                onChange={() => setProfileData(prev => ({ ...prev, is_public: !prev.is_public }))}
              />
              <span />
            </Toggle>
          </SectionTitle>
          <InfoBox>
            💡 When enabled, your public profile will be visible at: /agent/{profileData.agent_name?.toLowerCase().replace(/\s+/g, '-')}
          </InfoBox>
        </div>
      </ProfileSection>
    );
  }

  return (
    <ProfileSection>
      <SectionTitle>👤 Agent/Landlord Information</SectionTitle>
      
      <Form onSubmit={handleSubmit}>
        <SectionTitle>Basic Information</SectionTitle>
        <InputGroup>
          <Label>Full Name *</Label>
          <Input
            type="text"
            name="agent_name"
            value={profileData.agent_name}
            onChange={handleChange}
            required
          />
        </InputGroup>

        <InputGroup>
          <Label>Phone Number *</Label>
          <Input
            type="tel"
            name="agent_phone"
            value={profileData.agent_phone}
            onChange={handleChange}
            required
          />
        </InputGroup>

        <InputGroup>
          <Label>Email Address</Label>
          <Input
            type="email"
            name="agent_email"
            value={profileData.agent_email}
            onChange={handleChange}
          />
        </InputGroup>

        <InputGroup>
          <Label>License Number (if applicable)</Label>
          <Input
            type="text"
            name="agent_license"
            value={profileData.agent_license}
            onChange={handleChange}
          />
        </InputGroup>

        <SectionTitle style={{ marginTop: '20px' }}>Public Profile Details</SectionTitle>
        
        <InputGroup>
          <Label>Brokerage Name</Label>
          <Input
            type="text"
            name="brokerage_name"
            value={profileData.brokerage_name}
            onChange={handleChange}
          />
        </InputGroup>

        <InputGroup>
          <Label>Profile Photo URL</Label>
          <Input
            type="url"
            name="profile_photo_url"
            value={profileData.profile_photo_url}
            onChange={handleChange}
            placeholder="https://example.com/your-photo.jpg"
          />
        </InputGroup>

        <InputGroup>
          <Label>Bio</Label>
          <Textarea
            name="bio"
            value={profileData.bio}
            onChange={handleChange}
            placeholder="Tell potential clients about yourself and your expertise..."
          />
        </InputGroup>

        <InputGroup>
          <Label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            Enable Public Profile
            <Toggle>
              <input
                type="checkbox"
                checked={profileData.is_public}
                onChange={() => setProfileData(prev => ({ ...prev, is_public: !prev.is_public }))}
              />
              <span />
            </Toggle>
          </Label>
          {profileData.is_public && (
            <InfoBox style={{ marginTop: '10px' }}>
              Your public profile will be available at: /agent/{profileData.agent_name?.toLowerCase().replace(/\s+/g, '-')}
            </InfoBox>
          )}
        </InputGroup>

        <ButtonRow>
          <Button type="submit" className="primary">
            💾 Save Profile
          </Button>
          {hasProfile && (
            <Button type="button" className="secondary" onClick={handleCancel}>
              ❌ Cancel
            </Button>
          )}
        </ButtonRow>
      </Form>
      
      <InfoBox>
        💡 <strong>Your contact information will appear on all your property booking pages</strong> so interested buyers and agents can reach you directly. Name and phone are required.
      </InfoBox>
    </ProfileSection>
  );
};

export default AgentProfile;